import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Btn, Field, TextInput, Icons } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function currentMonth() {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function FxRatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [form, setForm] = useState({ month: currentMonth(), currency: "EUR", rate_to_usd: "" });
  const [autoFetching, setAutoFetching] = useState(false);

  const load = () => api.get(`/fx-rates?month=${month}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [month]);

  const save = async () => {
    try {
      await api.post("/fx-rates", { ...form, rate_to_usd: parseFloat(form.rate_to_usd) });
      toast.success("Saved"); load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const autoFetch = async () => {
    setAutoFetching(true);
    try {
      await api.post("/fx-rates/auto-fetch", { month: form.month, currency: form.currency });
      toast.success(`Fetched avg for ${form.currency} ${form.month}`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setAutoFetching(false); }
  };

  const del = async (id) => { await api.delete(`/fx-rates/${id}`); load(); };

  return (
    <AppShell
      title="FX Rates"
      subtitle="Monthly average currency rates to USD, used for invoice conversion"
      actions={<input data-testid="fx-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 rounded-sm bg-white border border-[var(--wv-border)] font-mono text-sm" />}
    >
      {isAdmin && (
        <div className="wv-card p-6 mb-6">
          <div className="wv-overline">Add / update rate</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            <Field label="Month"><TextInput type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} /></Field>
            <Field label="Currency"><TextInput data-testid="fx-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} placeholder="EUR" /></Field>
            <Field label="Rate → USD"><TextInput data-testid="fx-rate" type="number" step="0.000001" value={form.rate_to_usd} onChange={(e) => setForm({ ...form, rate_to_usd: e.target.value })} placeholder="1.087500" /></Field>
            <div className="flex items-end"><Btn data-testid="fx-save" onClick={save}>Save manual</Btn></div>
            <div className="flex items-end"><Btn variant="secondary" data-testid="fx-auto" onClick={autoFetch} disabled={autoFetching}>{autoFetching ? "Fetching..." : "Auto-fetch avg"}</Btn></div>
          </div>
        </div>
      )}

      <DataTable
        testid="fx-table"
        columns={[
          { key: "month", label: "Month", mono: true },
          { key: "currency", label: "Currency", mono: true },
          { key: "rate_to_usd", label: "Rate → USD", mono: true, render: (r) => Number(r.rate_to_usd).toFixed(6) },
          { key: "source", label: "Source" },
          { key: "updated_at", label: "Updated", render: (r) => r.updated_at?.slice(0, 19).replace("T", " ") },
        ]}
        rows={rows}
        actions={isAdmin ? (r) => <button onClick={() => del(r.id)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button> : null}
      />
    </AppShell>
  );
}
